import { Check, CheckCircle, Copy, Eye, RefreshCw } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { apiService } from '../services/api';

export const HeadsetPairingPage: React.FC = () => {
  const [pairingCode, setPairingCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isPaired, setIsPaired] = useState<boolean>(false);
  const [codeRefreshCount, setCodeRefreshCount] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);

  // Start pairing process
  const { mutate: startPairing, isLoading: isStartingPairing } = useMutation(
    () => apiService.startHardwarePairing(),
    {
      onSuccess: (data) => {
        setPairingCode(data.pairingCode);
        setIsPaired(data.paired || false);

        if (data.expiresAt) {
          const expiration = new Date(data.expiresAt);
          const now = new Date();
          const diff = Math.max(0, Math.floor((expiration.getTime() - now.getTime()) / 1000));
          setTimeRemaining(diff);
        }

        if (data.paired) {
          toast.success('Headset paired successfully!', { duration: 8000 });
        } else {
          toast.success('Pairing code generated successfully!', { duration: 4000 });
        }
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || 'Failed to start pairing process';
        toast.error(errorMessage);
      },
    }
  );

  // Controlled polling mutation for pairing status
  const { mutate: checkPairingStatus } = useMutation(
    () => apiService.startHardwarePairing(),
    {
      onSuccess: (data) => {
        if (data.paired) {
          setIsPaired(true);
          setPairingCode(''); // Clear the pairing code to show success state
          toast.success('Headset paired successfully!', { duration: 8000 });
          // Clear all intervals when paired
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
          isPollingRef.current = false;
        } else if (data.pairingCode && data.pairingCode !== pairingCode) {
          // Update with new code if different
          setPairingCode(data.pairingCode);
          if (data.expiresAt) {
            const expiration = new Date(data.expiresAt);
            const now = new Date();
            const diff = Math.max(0, Math.floor((expiration.getTime() - now.getTime()) / 1000));
            setTimeRemaining(diff);
          }
        }
        // Mark polling as complete
        isPollingRef.current = false;
      },
      onError: (error) => {
        console.error('Polling error:', error);
        // Mark polling as complete even on error to allow retry
        isPollingRef.current = false;
      },
    }
  );

  // Controlled polling effect - only poll when we have a pairing code and not paired
  useEffect(() => {
    if (pairingCode && !isPaired && !isPollingRef.current) {
      // Start polling every 5 seconds, but only if no request is pending
      pollingIntervalRef.current = setInterval(() => {
        if (!isPollingRef.current) {
          isPollingRef.current = true;
          checkPairingStatus();
        }
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pairingCode, isPaired, checkPairingStatus]);

  // Countdown timer effect
  useEffect(() => {
    if (timeRemaining > 0 && !isPaired) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Time expired, check if we should auto-refresh or reset
            if (codeRefreshCount < 2) {
              // Auto-refresh the pairing code
              setCodeRefreshCount(prev => prev + 1);
              startPairing();
              return 0; // Will be set by the mutation success handler
            } else {
              // Reset to initial state after 3 codes
              setPairingCode('');
              setCodeRefreshCount(0);
              toast.error('Pairing code has expired. Please generate a new one.');
              return 0;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timeRemaining, isPaired, codeRefreshCount, startPairing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  // Format pairing code for display (e.g., "A6FF05" -> "A6F-F05")
  const formatPairingCode = (code: string): string => {
    if (code.length === 6) {
      return `${code.slice(0, 3)}-${code.slice(3)}`;
    }
    return code;
  };

  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render pairing code with colored characters
  const renderPairingCode = (code: string) => {
    const formattedCode = formatPairingCode(code);
    return formattedCode.split('').map((char, index) => {
      const isLetter = /[A-Za-z]/.test(char);
      const isNumber = /[0-9]/.test(char);

      if (isLetter) {
        return (
          <span key={index} style={{ color: '#D97706' }}>
            {char}
          </span>
        );
      } else if (isNumber) {
        return (
          <span key={index} style={{ color: '#059669' }}>
            {char}
          </span>
        );
      } else {
        // Handle hyphens and other characters
        return (
          <span key={index} style={{ color: '#6B7280' }}>
            {char}
          </span>
        );
      }
    });
  };

  const handleStartPairing = () => {
    setCodeRefreshCount(0); // Reset refresh count when manually starting
    isPollingRef.current = false; // Reset polling state
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    startPairing();
  };


  const handleCopyCode = async () => {
    if (pairingCode) {
      try {
        await navigator.clipboard.writeText(pairingCode);
        setCopied(true);
        toast.success('Pairing code copied to clipboard!', { duration: 3000 });
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy pairing code');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-secondary-900">Headset Pairing</h1>
        <p className="mt-2 text-lg text-secondary-600">
          Connect your neurofeedback headset to the XRegulation platform
        </p>
      </div>

      {/* Pairing Code Generation or Success Display */}
      <div className="max-w-2xl mx-auto">
        <div className="card">
          {isPaired ? (
            // Successfully Paired Display
            <div className="text-center py-8">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-green-100 rounded-full">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-green-900 mb-3">Successfully Paired!</h2>
              <p className="text-lg text-green-700 mb-6">
                Your VR headset has been successfully connected to the XRegulation platform.
              </p>
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-600">
                  Your headset is now ready to use for neurofeedback sessions. You can start a new session from the Dashboard.
                </p>
              </div>
              <button
                onClick={() => {
                  setIsPaired(false);
                  setPairingCode('');
                  setCodeRefreshCount(0);
                  setTimeRemaining(0);
                  isPollingRef.current = false;
                  if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                  }
                }}
                className="btn-secondary"
              >
                Pair Another Headset
              </button>
            </div>
          ) : (
            // Pairing Code Generation Interface
            <>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Eye className="h-6 w-6 text-primary-600" />
                </div>
                <h2 className="text-xl font-semibold text-secondary-900">Generate Pairing Code</h2>
              </div>

              <div className="space-y-4">
                <p className="text-secondary-600">
                  Click the button below to generate a unique pairing code for your headset.
                </p>

                {pairingCode ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-secondary-50 rounded-lg border-2 border-dashed border-secondary-300">
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <p className="text-sm text-secondary-500">
                            Your Pairing Code {codeRefreshCount > 0 && `(Attempt ${codeRefreshCount + 1}/3)`}
                          </p>
                          {timeRemaining > 0 && (
                            <div className={`px-2 py-1 rounded text-xs font-mono ${timeRemaining <= 15
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                              }`}>
                              {formatTimeRemaining(timeRemaining)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <code className="text-2xl font-mono font-bold">
                            {renderPairingCode(pairingCode)}
                          </code>
                          <button
                            onClick={handleCopyCode}
                            className="p-2 text-secondary-500 hover:text-primary-600 transition-colors"
                          >
                            {copied ? (
                              <Check className="h-5 w-5 text-green-600" />
                            ) : (
                              <Copy className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center space-x-6 text-xs text-secondary-500">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: '#D97706' }}></div>
                        <span>Letters</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: '#059669' }}></div>
                        <span>Numbers</span>
                      </div>
                    </div>
                    <p className="text-sm text-secondary-500 text-center">
                      Enter this code on your headset device to complete the pairing process.
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleStartPairing}
                    disabled={isStartingPairing}
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                  >
                    {isStartingPairing ? (
                      <RefreshCw className="h-5 w-5 animate-spin" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                    <span>{isStartingPairing ? 'Generating...' : 'Generate Pairing Code'}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Pairing Instructions</h3>
        <div className="space-y-3 text-secondary-600">
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">1</span>
            <p>Make sure your headset is powered on and in pairing mode.</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">2</span>
            <p>Click "Generate Pairing Code" to create a unique code for your headset.</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">3</span>
            <p>Enter the pairing code on your headset device when prompted.</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">4</span>
            <p>Your headset will automatically complete the pairing process with the server.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
